import moment from 'moment';

/**
 * Build OCC-style quote key used in the options websocket/redux store.
 */
export function buildOptionTicker(item) {
    if (!item?.symbol || !item?.expire_at || !item?.option_kind) {
        return null;
    }
    const expiration = moment(item.expire_at).zone('+00:00').format('YYMMDD');
    const kind = item.option_kind === 'CALL' ? 'C' : 'P';
    const strike = (Number(item.strike) || 0) * 1000;
    return `O:${item.symbol}${expiration}${kind}${strike.toFixed(0).padStart(8, '0')}`;
}

/**
 * Last option premium from live quotes, falling back to stored option_price.
 * Returns a finite number or null when unavailable.
 */
export function getOptionLastPrice(item, optionQuotes = {}) {
    const ticker = buildOptionTicker(item);
    const quote = (ticker && optionQuotes[ticker]?.c) ?? item?.option_price;
    const price = Number(quote);
    if (!Number.isFinite(price) || price < 0) {
        return null;
    }
    return +price.toFixed(2);
}

/**
 * Unrealized P/L for a single option leg.
 * open_price is stored signed (BUY negative, SELL positive).
 */
export function calcOptionLegPl(item, lastPrice) {
    if (lastPrice == null || !Number.isFinite(lastPrice) || lastPrice < 0) {
        return null;
    }
    const qty = Number(item?.quantity) || 0;
    const open = Number(item?.open_price);
    if (!qty || !Number.isFinite(open)) {
        return null;
    }

    const perContract = item?.market === 'SELL'
        ? open - lastPrice
        : open + lastPrice;

    return perContract * qty * 100;
}

export function calcOptionLegPlPercent(item, lastPrice) {
    if (lastPrice == null || !Number.isFinite(lastPrice) || lastPrice < 0) {
        return null;
    }
    const open = Number(item?.open_price);
    const absOpen = Math.abs(open);
    if (!Number.isFinite(open) || !absOpen) {
        return null;
    }
    const perContract = item?.market === 'SELL'
        ? open - lastPrice
        : open + lastPrice;
    return (perContract / absOpen) * 100;
}

function walkLeafLegs(node, visit) {
    if (!node) {
        return;
    }
    const children = Array.isArray(node.sub_order) ? node.sub_order : [];
    const isLeaf = Number(node.counter) === 1 || children.length === 0;
    if (isLeaf && node.option_kind) {
        visit(node);
        return;
    }
    children.forEach((child) => walkLeafLegs(child, visit));
}

/**
 * Aggregate unrealized P/L across nested option groups for one symbol/strategy.
 */
export function getOrderOptionPl(order, optionQuotes = {}) {
    if (!order) {
        return null;
    }

    if (Number(order.counter) === 1 || !Array.isArray(order.sub_order) || order.sub_order.length === 0) {
        return calcOptionLegPl(order, getOptionLastPrice(order, optionQuotes));
    }

    let total = 0;
    let hasValue = false;
    walkLeafLegs(order, (leg) => {
        const pl = calcOptionLegPl(leg, getOptionLastPrice(leg, optionQuotes));
        if (pl != null) {
            total += pl;
            hasValue = true;
        }
    });
    return hasValue ? total : null;
}

export function getOrderOptionPlPercent(order, optionQuotes = {}) {
    if (!order) {
        return null;
    }

    if (Number(order.counter) === 1 || !Array.isArray(order.sub_order) || order.sub_order.length === 0) {
        return calcOptionLegPlPercent(order, getOptionLastPrice(order, optionQuotes));
    }

    const pl = getOrderOptionPl(order, optionQuotes);
    let invested = 0;
    walkLeafLegs(order, (leg) => {
        invested += Math.abs(Number(leg.open_trade) || 0);
    });
    if (pl == null || !invested) {
        return null;
    }
    return (pl / invested) * 100;
}

/**
 * Net last premium for a multi-leg strategy (SELL legs subtract).
 */
export function getStrategyNetLastPrice(rows, optionQuotes = {}) {
    if (!Array.isArray(rows) || !rows.length) {
        return null;
    }

    let total = 0;
    let hasValue = false;
    rows.forEach((item) => {
        walkLeafLegs(item, (leg) => {
            const price = getOptionLastPrice(leg, optionQuotes);
            if (price == null) {
                return;
            }
            total += leg.market === 'SELL' ? -price : price;
            hasValue = true;
        });
    });
    return hasValue ? +total.toFixed(2) : null;
}

export function formatPl(value) {
    if (value == null || Number.isNaN(value) || !Number.isFinite(value)) {
        return null;
    }
    return Number(value).toFixed(2);
}
